import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';

// kendo
import {useLocalization} from "@progress/kendo-react-intl";
import {Grid, GridColumn} from "@progress/kendo-react-grid";
import {Checkbox} from "@progress/kendo-react-inputs";
import {filterBy, orderBy, process} from "@progress/kendo-data-query";

// components
import UserContext from "../../common/UserContext";
import {ColumnMenu, DisableRowRender, NoWrapCell} from "../../common/Grid";
import {AssignQtyCell} from "./AssignQty";

// multilingual
import {
    productNumberKey,
    descriptionKey,
    assignQtyKey,
    qtyAvailableKey,
    infoKey,
    incompatibleProductsSelectedKey,
    mainMessages
} from "../../../assets/text/MultilingualText";
import KSMPopover from "../../common/Popovers";

const uniqueID = 'unique_id'
const filterID = 'hostid_type'

export default function UnassignedProductsGrid(props) {
    const {
        siteLanguageDefault
    } = useContext(UserContext);
    const localization = useLocalization();

    const {
        unassigned,
        setUnassigned,
        setHostIDType,
        selectHeaderCheck,
        setSelectHeaderCheck,
        currentType
    } = props

    const productsRef = useRef(unassigned);

    //States and functions for pagination
    const initialGridState = {
        take: 10, skip: 0,
    };
    const [gridState, setGridState] = useState(initialGridState);
    const gridStateRef = useRef(initialGridState)
    const [dataState, setDataState] = useState(
        process(unassigned
            .map((item) => ({
                ...item,
            })), initialGridState)
    );

    const onDataStateChange = useCallback((event) => {
        const newDataState = process(
            unassigned
                .map((item) => ({
                    ...item,
                })),
            event.dataState
        );
        gridStateRef.current = event.dataState
        setGridState(event.dataState);
        setDataState(newDataState);
    }, [unassigned]);

    useEffect(() => {
        const newDataState = process(
            unassigned
                .map((item) => ({
                    ...item,
                })),
            gridState
        );
        setDataState(newDataState);
    }, [unassigned]) // eslint-disable-line react-hooks/exhaustive-deps

    //Cell and functions for selection
    const SelectCell = (props) => {
        const anchor = useRef(null);
        const [popoverShow, setPopoverShow] = useState(false);

        // show popover if there is an error on mouse over
        const onMouseOver = (ev) => {
            setPopoverShow(true);
        };

        // remove popover on mouse leave
        const onMouseOut = (ev) => {
            let isChildEl = ev.currentTarget.contains(ev.relatedTarget);
            if (!isChildEl) setPopoverShow(false);
        };

        return <td
            ref={anchor}
            onMouseOver={onMouseOver}
            onMouseOut={onMouseOut}
        >
            {props.dataItem.disabled && //Need CSS pointer-events: auto;
                <KSMPopover
                    show={popoverShow}
                    anchor={anchor.current}
                    position={'right'}
                    style={{width: '30rem'}}
                    type={'info'}
                    message={<div>
                        <b>{localization.toLanguageString(infoKey, mainMessages[siteLanguageDefault][infoKey])}</b>
                        <br/>
                        {localization.toLanguageString(incompatibleProductsSelectedKey, mainMessages[siteLanguageDefault][incompatibleProductsSelectedKey])}
                    </div>}
                />
            }
            <Checkbox
                type="checkbox"
                onChange={props.selectionChange}
                value={props.dataItem.selected}
                disabled={props.dataItem.disabled}
            />
        </td>
    }

    const disableHeaderClass = 'ksm-deactivate-button-old'
    const [selectHeaderClass, setSelectHeaderClass] = useState(disableHeaderClass)

    //disable or enable header and select all header based on filtered list
    useEffect(() => {
        let filteredData = filterBy(unassigned, gridStateRef.current.filter)
        let selectedCount = filteredData.filter(item => item.selected).length
        let typeCount

        //check all rows has the same host type
        let sameType = true
        if (currentType.current === null) {
            let defaultType = null
            if (filteredData.length > 0) {
                defaultType = filteredData[0][filterID]
                for (const item of filteredData) {
                    if (item[filterID] !== defaultType) {
                        sameType = false
                    }
                }
            } else {
                sameType = false
            }

            if (sameType) {
                currentType.current = defaultType
                setSelectHeaderClass('')
            } else {
                setSelectHeaderClass(disableHeaderClass)
            }
            setSelectHeaderCheck(false)
        } else {
            //count number of types in filtered list and set defaults
            typeCount = filteredData.filter(item => item[filterID] === currentType.current).length
            if (typeCount === 0) {
                typeCount = -1
            }
            //set header checked and disabled value
            setSelectHeaderCheck(selectedCount === typeCount)
            if (typeCount < 1) {
                setSelectHeaderClass(disableHeaderClass)
            } else {
                setSelectHeaderClass('')
            }
        }
    }, [unassigned, dataState]) // eslint-disable-line react-hooks/exhaustive-deps

    const onSelectionChange = useCallback((event) => {
        const checked = event.syntheticEvent.target.checked
        if (currentType.current === null) {
            currentType.current = event.dataItem[filterID]
        }
        let newUnassigned
        let selected = false
        newUnassigned = unassigned.map(item => {
            if (event.dataItem[uniqueID] === item[uniqueID]) {
                item.selected = checked
            }

            item.disabled = item[filterID] !== currentType.current;

            //check if any rows are still selected in whole list
            if (item.selected) {
                selected = true
            }
            return item
        })

        // reset current host type and enable all rows
        if (!selected) {
            currentType.current = null
            newUnassigned = newUnassigned.map(item => {
                item.disabled = false
                return item
            })
        }

        setUnassigned(newUnassigned)
        setHostIDType(currentType.current)
    }, [unassigned]); // eslint-disable-line react-hooks/exhaustive-deps

    const onHeaderSelectionChange = useCallback(event => {
        const checked = event.nativeEvent.target.checked;
        let selected = false

        let newUnassigned = unassigned.map(item => {
            //Change selected based only on the filtered list
            for (const filtered of filterBy(unassigned, gridStateRef.current.filter)) {
                if (item[uniqueID] === filtered[uniqueID]) {
                    item.selected = checked && item[filterID] === currentType.current
                }
            }

            //check if any rows are still selected in whole list
            if (item.selected) {
                selected = true
            }
            return item
        });

        if (!selected) {
            currentType.current = null
        }

        //disable rows based on if no rows have been selected
        newUnassigned = newUnassigned.map(item => {
            if (!selected) {
                item.disabled = false
            } else {
                item.disabled = item[filterID] !== currentType.current
            }
            return item
        })

        setUnassigned(newUnassigned)
        setHostIDType(currentType.current)
    }, [unassigned]); // eslint-disable-line react-hooks/exhaustive-deps

    // update product state for editable assign qty in license table
    const productItemChange = (e) => {
        let copyProducts = productsRef.current.map((product) => {
            if (product[uniqueID] === e.dataItem[uniqueID]) {
                let setValue = e.value

                // assign qty cannot be greater than the max qty available
                if (e.value > parseInt(e.dataItem.qty_available)) {
                    setValue = parseInt(e.dataItem.qty_available)
                }
                // assign qty cannot be less than 1
                if (e.value < 1) {
                    setValue = null
                }

                product[e.field] = setValue;
            }
            return product;
        });
        let orderByDisabled = orderBy(copyProducts, [{field: 'disabled', dir: 'asc'}]);
        let orderBySelected = orderBy(orderByDisabled, [{field: 'selected', dir: 'desc'}]);
        setUnassigned(orderBySelected);
    };

    return (
        <Grid
            className={'unassigned-checkout-grid'}
            scrollable={'none'}
            data={dataState}
            sortable={true}
            //pagination
            pageable={(unassigned.length <= initialGridState.take) ? false : {
                buttonCount: 5,
                pageSizes: [10, 20, 50, 100],
            }}
            onDataStateChange={onDataStateChange}
            total={dataState.total}
            //select checkboxes
            onHeaderSelectionChange={onHeaderSelectionChange}
            onSelectionChange={onSelectionChange}
            selectedField={'selected'}
            selectable={{
                enabled: false,
                drag: false,
                cell: false,
                mode: 'multiple'
            }}
            //disable rows
            rowRender={DisableRowRender}
            // assign qty cell change
            onItemChange={productItemChange}
            {...gridState}
        >
            <GridColumn
                field={'selected'}
                cell={SelectCell}
                headerSelectionValue={selectHeaderCheck}
                headerClassName={selectHeaderClass}
            />
            <GridColumn
                field="product_number"
                title={localization.toLanguageString(productNumberKey, mainMessages[siteLanguageDefault][productNumberKey])}
                columnMenu={ColumnMenu}
                cell={NoWrapCell}
            />
            <GridColumn
                field="product_desc"
                title={localization.toLanguageString(descriptionKey, mainMessages[siteLanguageDefault][descriptionKey])}
                columnMenu={ColumnMenu}
            />
            <GridColumn
                field="qty_requested"
                title={localization.toLanguageString(assignQtyKey, mainMessages[siteLanguageDefault][assignQtyKey])}
                sortable={false}
                cell={AssignQtyCell}
            />
            <GridColumn
                field="qty_available"
                title={localization.toLanguageString(qtyAvailableKey, mainMessages[siteLanguageDefault][qtyAvailableKey])}
            />
        </Grid>
    )
}